import { useCallback, useState } from "react";
import { approveHitlItem, assignHitlItem, rejectHitlItem, requestHitlChanges } from "@/api/hitl";
import { useToast } from "@/components/ToastProvider";
import { useCapabilities } from "@/hooks/useCapabilities";
import { useUpgradeModal } from "@/hooks/useUpgradeModal";
import type { CapabilityFlag } from "@/types/capabilities";

type HitlActionKey = "approve" | "reject" | "request_changes" | "assign";

const ACTION_CAPS: Record<HitlActionKey, CapabilityFlag> = {
  approve: "canApproveHitl",
  reject: "canRejectHitl",
  request_changes: "canRequestChangesHitl",
  assign: "canAssignHitl",
};

export function useHitlActions(defaultId?: string | null) {
  const { push } = useToast();
  const { capabilities } = useCapabilities();
  const { openUpgradeModal } = useUpgradeModal();
  const [pendingAction, setPendingAction] = useState<HitlActionKey | null>(null);

  const ensureId = useCallback(
    (overrideId?: string | null) => {
      const target = overrideId ?? defaultId ?? null;
      if (!target) {
        throw new Error("HITL id is required for this action");
      }
      return target;
    },
    [defaultId],
  );

  const ensureCapability = useCallback(
    (action: HitlActionKey) => {
      const flag = ACTION_CAPS[action];
      if (flag && !capabilities[flag]) {
        openUpgradeModal(action);
        throw new Error("requires_upgrade");
      }
    },
    [capabilities, openUpgradeModal],
  );

  const run = useCallback(
    async <T,>(action: HitlActionKey, fn: () => Promise<T>, successMessage?: string) => {
      setPendingAction(action);
      try {
        const result = await fn();
        if (successMessage) {
          push({ title: "HITL updated", message: successMessage });
        }
        return result;
      } catch (error) {
        const message = error instanceof Error ? error.message : "Action failed.";
        push({ title: "Unable to update", message });
        throw error;
      } finally {
        setPendingAction((current) => (current === action ? null : current));
      }
    },
    [push],
  );

  const approve = useCallback(
    async (overrides?: { body?: Record<string, unknown>; id?: string }) => {
      ensureCapability("approve");
      const id = ensureId(overrides?.id);
      await run("approve", () => approveHitlItem(id, overrides?.body), "Item approved.");
    },
    [ensureId, run, ensureCapability],
  );

  const reject = useCallback(
    async (reason?: string, overrideId?: string) => {
      ensureCapability("reject");
      const id = ensureId(overrideId);
      await run("reject", () => rejectHitlItem(id, reason), "Item rejected.");
    },
    [ensureId, run, ensureCapability],
  );

  const requestChanges = useCallback(
    async (notes: string, overrideId?: string) => {
      ensureCapability("request_changes");
      const id = ensureId(overrideId);
      await run("request_changes", () => requestHitlChanges(id, notes), "Requested changes sent.");
    },
    [ensureId, run, ensureCapability],
  );

  const assignTo = useCallback(
    async (userId: string | null | undefined, overrideId?: string) => {
      ensureCapability("assign");
      const id = ensureId(overrideId);
      await run("assign", () => assignHitlItem(id, userId), userId ? "Assigned successfully." : "Assignment cleared.");
    },
    [ensureId, run, ensureCapability],
  );

  return {
    approve,
    reject,
    requestChanges,
    assignTo,
    pendingAction,
  };
}
